import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Mail, Save, Send, Server, KeyRound, Loader2 } from 'lucide-react';
import { emailSettingsAPI } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { MaybeLayout } from '../components/layout/MaybeLayout';

const EVENT_LABELS = {
  workflow_auto_started: 'Workflow automatisch gestartet',
  contract_expiring: 'Vertragsablauf-Erinnerung',
  cleaning_cost_summary: 'Reinigungskosten (Monatsübersicht)',
  vacation_request: 'Neuer Urlaubsantrag',
  correction_request: 'Neuer Antrag / Änderungsantrag',
};

export default function EmailSettings({ embedded = false }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailSettingsAPI.get();
      setCfg(res.data);
    } catch {
      toast.error('Fehler beim Laden der E-Mail-Einstellungen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setProvider = (v) => setCfg((c) => ({ ...c, provider_type: v }));
  const setSmtp = (k, v) => setCfg((c) => ({ ...c, smtp: { ...c.smtp, [k]: v } }));
  const setSg = (k, v) => setCfg((c) => ({ ...c, sendgrid: { ...c.sendgrid, [k]: v } }));
  const setEvent = (k, v) => setCfg((c) => ({ ...c, events: { ...c.events, [k]: v } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        provider_type: cfg.provider_type,
        smtp: { ...cfg.smtp, port: cfg.smtp.port ? parseInt(cfg.smtp.port) : null },
        sendgrid: cfg.sendgrid,
        events: cfg.events,
      };
      const res = await emailSettingsAPI.update(payload);
      setCfg(res.data);
      toast.success('E-Mail-Einstellungen gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Bitte Empfänger-Adresse eingeben'); return; }
    setTesting(true);
    try {
      await emailSettingsAPI.sendTest(testEmail);
      toast.success('Test-E-Mail wurde versendet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test-Versand fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lädt…
        </div>
      </MaybeLayout>
    );
  }

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="email-settings-page">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" /> E-Mail-Einstellungen
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Flexibler E-Mail-Versand pro Firma – eigener SMTP-Host oder verwalteter Anbieter (SendGrid).
          </p>
        </div>

        {/* Versandweg */}
        <Card data-testid="email-provider-card">
          <CardHeader><CardTitle className="text-lg font-heading">Versandweg</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="max-w-sm space-y-2">
              <Label>Methode</Label>
              <Select value={cfg.provider_type} onValueChange={setProvider}>
                <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Deaktiviert (nur In-App)</SelectItem>
                  <SelectItem value="smtp">Eigener SMTP-Host</SelectItem>
                  <SelectItem value="sendgrid">Verwalteter Anbieter (SendGrid)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {cfg.provider_type === 'smtp' && (
              <div className="rounded-lg border bg-muted/20 p-4 space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2"><Server className="h-4 w-4" />SMTP-Server</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Host</Label>
                    <Input value={cfg.smtp.host} onChange={(e) => setSmtp('host', e.target.value)} placeholder="smtp.firma.de" data-testid="smtp-host-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Port</Label>
                    <Input type="number" value={cfg.smtp.port ?? ''} onChange={(e) => setSmtp('port', e.target.value)} placeholder="587" data-testid="smtp-port-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Verschlüsselung</Label>
                    <Select value={cfg.smtp.security} onValueChange={(v) => setSmtp('security', v)}>
                      <SelectTrigger data-testid="smtp-security-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starttls">STARTTLS (Port 587)</SelectItem>
                        <SelectItem value="ssl">SSL/TLS (Port 465)</SelectItem>
                        <SelectItem value="none">Keine</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Benutzername</Label>
                    <Input value={cfg.smtp.username} onChange={(e) => setSmtp('username', e.target.value)} autoComplete="off" data-testid="smtp-username-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Passwort</Label>
                    <Input type="password" value={cfg.smtp.password} onChange={(e) => setSmtp('password', e.target.value)} autoComplete="new-password" data-testid="smtp-password-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Absender-Adresse</Label>
                    <Input value={cfg.smtp.from_email} onChange={(e) => setSmtp('from_email', e.target.value)} placeholder="noreply@firma.de" data-testid="smtp-from-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Absender-Name</Label>
                    <Input value={cfg.smtp.from_name} onChange={(e) => setSmtp('from_name', e.target.value)} placeholder="TeamHub" data-testid="smtp-from-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Antwort-Adresse (optional)</Label>
                    <Input value={cfg.smtp.reply_to} onChange={(e) => setSmtp('reply_to', e.target.value)} placeholder="hr@firma.de" data-testid="smtp-replyto-input" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Tipp: Für gute Zustellbarkeit SPF/DKIM der Absenderdomain einrichten.</p>
              </div>
            )}

            {cfg.provider_type === 'sendgrid' && (
              <div className="rounded-lg border bg-muted/20 p-4 space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2"><KeyRound className="h-4 w-4" />SendGrid</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-3">
                    <Label>API-Key</Label>
                    <Input type="password" value={cfg.sendgrid.api_key} onChange={(e) => setSg('api_key', e.target.value)} autoComplete="new-password" placeholder="SG.xxxxx" data-testid="sendgrid-apikey-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Absender-Adresse</Label>
                    <Input value={cfg.sendgrid.from_email} onChange={(e) => setSg('from_email', e.target.value)} placeholder="noreply@firma.de" data-testid="sendgrid-from-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Absender-Name</Label>
                    <Input value={cfg.sendgrid.from_name} onChange={(e) => setSg('from_name', e.target.value)} placeholder="TeamHub" data-testid="sendgrid-from-name-input" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Die Absender-Adresse muss in SendGrid verifiziert sein (Single Sender oder Domain Authentication).</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ereignisse */}
        <Card data-testid="email-events-card">
          <CardHeader><CardTitle className="text-lg font-heading">Welche Ereignisse per E-Mail?</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {Object.keys(EVENT_LABELS).map((k) => (
              <div key={k} className="flex items-center justify-between border-b last:border-0 pb-3 last:pb-0">
                <span className="text-sm">{EVENT_LABELS[k]}</span>
                <Switch
                  checked={!!cfg.events[k]}
                  onCheckedChange={(v) => setEvent(k, v)}
                  disabled={cfg.provider_type === 'off'}
                  data-testid={`email-event-${k}`}
                />
              </div>
            ))}
            {cfg.provider_type === 'off' && (
              <p className="text-xs text-muted-foreground">Wähle oben einen Versandweg, um Ereignisse zu aktivieren.</p>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button onClick={handleSave} disabled={saving} data-testid="email-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Speichern
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@firma.de" className="sm:w-56" data-testid="email-test-input" />
            <Button variant="outline" onClick={handleTest} disabled={testing || cfg.provider_type === 'off'} data-testid="email-test-btn">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Test senden
            </Button>
          </div>
        </div>
      </div>
    </MaybeLayout>
  );
}
